import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/AdminSharedComponents";
import {
  Image,
  FileText,
  FileSpreadsheet,
  File,
  Download,
  Edit,
  ArrowLeft,
  Folder,
  Calendar,
  Loader2,
} from "lucide-react";

const ShowReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attachmentError, setAttachmentError] = useState({});
  const [userRole, setUserRole] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Fetch user role
  const fetchUserRole = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const role = user?.role || "admin";
      setUserRole(role);
    } catch (error) {
      console.error("Error fetching user role:", error);
      setUserRole("admin");
    }
  };

  // Fetch report data
  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch report");
      }

      const data = await response.json();
      const reportData = data.report || data;

      setReport(reportData);

      if (reportData.id) {
        await fetchAttachments(reportData.id);
      }

      if (reportData.project_id) {
        fetchProject(reportData.project_id);
      }
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to load report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch attachments using the correct route
  const fetchAttachments = async (reportId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/report-attachment/report/${reportId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const attachmentsData = await response.json();
        console.log("Fetched attachments:", attachmentsData);

        setReport((prevReport) => ({
          ...prevReport,
          attachments: Array.isArray(attachmentsData)
            ? attachmentsData
            : attachmentsData.attachments || [],
        }));
      } else {
        console.warn("Failed to fetch attachments:", response.status);
      }
    } catch (err) {
      console.warn("Error fetching attachments:", err.message);
    }
  };

  // Fetch project data
  const fetchProject = async (projectId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/projects/${projectId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const projectData = await response.json();
        setProject(projectData.project || projectData);
      }
    } catch (err) {
      console.error("Error fetching project:", err);
    }
  };

  // Role-based access control
  const isSupervisor = userRole === "supervisor";
  const hasEditAccess = !isSupervisor;

  // Update isMobile state when window is resized
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch data on component mount
  useEffect(() => {
    fetchUserRole();
    if (id) {
      fetchReport();
    }
  }, [id]);

  const handleDownload = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reports/${report.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (!response.ok) throw new Error("Gagal mengunduh laporan");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.title.replace(/\s+/g, "_")}_full_report.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert("Terjadi kesalahan saat mengunduh laporan lengkap.");
      console.error(error);
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) {
      return <Image className="h-5 w-5" />;
    } else if (fileType === "application/pdf") {
      return <FileText className="h-5 w-5" color="#E53935" />;
    } else if (
      fileType.includes("excel") ||
      fileType.includes("spreadsheet") ||
      fileType.includes("xls")
    ) {
      return <FileSpreadsheet className="h-5 w-5" color="#1D6F42" />;
    } else {
      return <File className="h-5 w-5" />;
    }
  };

  // Format file size
  const formatFileSize = (size) => {
    if (size < 1024) {
      return size + " B";
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + " KB";
    } else {
      return (size / (1024 * 1024)).toFixed(2) + " MB";
    }
  };

  // Truncate filename for display
  const truncateFileName = (filename, maxLength = 30) => {
    if (!filename || filename.length <= maxLength) return filename;

    const extension = filename.split(".").pop();
    const nameWithoutExt = filename.substring(0, filename.lastIndexOf("."));
    const truncatedName = nameWithoutExt.substring(
      0,
      maxLength - extension.length - 4
    );

    return `${truncatedName}...${extension}`;
  };

  // Get consistent attachment URL for both display and download
  const getAttachmentUrl = (attachment) => {
    const baseUrl = process.env.REACT_APP_API_URL || "";
    return `${baseUrl}/uploads/reports/${attachment.filename}`;
  };

  // Handle attachment download with error handling and fallback
  const handleAttachmentDownload = async (attachment) => {
    try {
      // Try to use the new attachment API if available
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/report-attachments/${attachment.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const attachmentData = await response.json();
          const attachmentUrl = getAttachmentUrl(
            attachmentData.attachment || attachmentData
          );

          const fileResponse = await fetch(attachmentUrl, { method: "HEAD" });
          if (fileResponse.ok) {
            const a = document.createElement("a");
            a.href = attachmentUrl;
            a.download = attachment.originalname || "download";
            a.setAttribute("target", "_blank");
            a.setAttribute("rel", "noopener noreferrer");
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
          }
        }
      } catch (apiError) {
        console.info("Attachment API not available, using direct download");
      }

      // Fallback to direct URL download
      const attachmentUrl = getAttachmentUrl(attachment);

      const fileResponse = await fetch(attachmentUrl, { method: "HEAD" });
      if (fileResponse.ok) {
        const a = document.createElement("a");
        a.href = attachmentUrl;
        a.download = attachment.originalname || "download";
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        throw new Error("File not found on server");
      }
    } catch (error) {
      console.error("Download error:", error);
      alert(
        `Error downloading the file "${attachment.originalname}": ${error.message}`
      );
    }
  };

  // Handle image loading error
  const handleImageError = (id) => {
    setAttachmentError((prev) => ({ ...prev, [id]: true }));
    console.error(`Failed to load image for attachment ID: ${id}`);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading report...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate(-1)} variant="secondary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // No report found
  if (!report) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Report not found</p>
          <Button onClick={() => navigate(-1)} variant="secondary">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  // Categorize attachments by type
  const imageAttachments = report.attachments
    ? report.attachments.filter((attachment) =>
        attachment.mimetype.startsWith("image/")
      )
    : [];
  const documentAttachments = report.attachments
    ? report.attachments.filter(
        (attachment) => !attachment.mimetype.startsWith("image/")
      )
    : [];

  // Check if report has attachments
  const hasAttachments = report.attachments && report.attachments.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header with actions */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button
                variant="text"
                onClick={() => navigate(-1)}
                className="!p-2 flex-shrink-0"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-xl sm:text-2xl font-semibold text-primary-dark truncate">
                {report.title}
              </h1>
            </div>

            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <Button
                variant="primary"
                className="flex items-center gap-2 !py-2 text-sm whitespace-nowrap"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4" />
                Download ZIP
              </Button>

              {hasEditAccess && (
                <Button
                  variant="secondary"
                  className="flex items-center gap-2 !py-2 text-sm whitespace-nowrap"
                  onClick={() => navigate(`/admin/reports/edit/${report.id}`)}
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Report metadata */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-3">
            {project && (
              <div className="text-sm flex items-center gap-1 bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-full">
                <Folder className="h-4 w-4" />
                <span className="truncate max-w-40">{project.name}</span>
              </div>
            )}
            {project?.status && (
              <div
                className={`text-sm px-3 py-1 rounded-full whitespace-nowrap ${
                  project.status === "Completed"
                    ? "bg-green-100 text-green-800"
                    : project.status === "In Progress"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-yellow-100 text-yellow-800"
                }`}
              >
                {project.status}
              </div>
            )}
            <div className="text-sm text-gray-500 flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full whitespace-nowrap">
              <Calendar className="h-4 w-4" />
              {new Date(report.created_at).toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
        </div>

        {/* Report content */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Description
            </h3>
            <div className="prose max-w-none">
              <p className="text-gray-600 whitespace-pre-line leading-relaxed break-words">
                {report.description}
              </p>
            </div>
          </div>

          {/* Report content section */}
          {report.content && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Report Content
              </h3>
              <div className="prose max-w-none">
                <div className="text-gray-600 whitespace-pre-line leading-relaxed break-words">
                  {report.content}
                </div>
              </div>
            </div>
          )}

          {/* Attachments */}
          {hasAttachments && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Attachments
              </h3>

              {/* Document attachments */}
              {documentAttachments.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">Documents</h4>
                  <div className="space-y-2">
                    {documentAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 bg-white rounded border"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="flex-shrink-0">
                            {getFileIcon(attachment.mimetype)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p
                              className="font-medium text-sm truncate"
                              title={attachment.originalname}
                            >
                              {truncateFileName(
                                attachment.originalname,
                                isMobile ? 25 : 100
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(attachment.size)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAttachmentDownload(attachment)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm px-3 py-1 rounded hover:bg-blue-50 flex-shrink-0 ml-2"
                        >
                          <Download className="h-4 w-4" />
                          <span className="hidden sm:inline">Download</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Image Attachments with Preview */}
              {imageAttachments.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">Images</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {imageAttachments.map((attachment) => (
                      <div
                        key={`attachment-${attachment.id}`}
                        className="border rounded-lg overflow-hidden bg-white"
                      >
                        <div className="bg-gray-50 p-3 border-b flex justify-between items-center gap-2">
                          <p
                            className="font-medium text-sm truncate flex-1 min-w-0"
                            title={attachment.originalname}
                          >
                            {truncateFileName(
                              attachment.originalname,
                              isMobile ? 20 : 30
                            )}
                          </p>
                          <button
                            onClick={() => handleAttachmentDownload(attachment)}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm flex-shrink-0"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                        {!attachmentError[attachment.id] ? (
                          <img
                            src={getAttachmentUrl(attachment)}
                            alt={attachment.originalname}
                            className="w-full h-auto max-h-80 object-contain p-2"
                            onError={() => handleImageError(attachment.id)}
                            onLoad={() => {
                              setAttachmentError((prev) => {
                                const newState = { ...prev };
                                delete newState[attachment.id];
                                return newState;
                              });
                            }}
                          />
                        ) : (
                          <div className="h-32 flex flex-col items-center justify-center text-red-500 bg-red-50 gap-2">
                            <p className="text-sm">Unable to load image</p>
                            <button
                              onClick={() =>
                                handleAttachmentDownload(attachment)
                              }
                              className="text-blue-600 hover:text-blue-800 text-xs underline"
                            >
                              Try Download
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShowReport;
